import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Camera, Facebook, Instagram, Mail, MapPin, MessageCircle } from "lucide-react";
import { useSubmissionsOpen } from "../hooks/useSubmissionsOpen";

const Footer = () => {
  const location = useLocation();
  const { open: submissionsOpen } = useSubmissionsOpen();
  // Hide footer on isolated judge routes
  if (location.pathname.startsWith('/judge') && location.pathname !== '/judges') {
    return null;
  }
  return (
    <footer className="bg-slate-800/50 md:bg-slate-900/95 backdrop-blur-sm border-t border-blue-900/20 mt-16" role="contentinfo" aria-label="Footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Logo and Description */}
          <div className="col-span-1 sm:col-span-2">
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-1">
                <div className="bg-gradient-to-r from-blue-400 to-cyan-400 p-2 rounded-lg">
                  <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <span className="text-lg sm:text-xl font-bold text-white">WUMA</span>
              </div>
              <p className="text-gray-400 text-sm ml-12">World Underwater Macro Awards</p>
            </div>
            <p className="text-gray-300 mb-4 max-w-md text-sm sm:text-base">
              Celebrating the art of underwater macro photography through annual competitions 
              that showcase the finest close-up imagery of marine life.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61584066810609" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-400 transition-colors"
                aria-label="Visit our Facebook page"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
              <a 
                href="https://www.instagram.com/wuma.awards" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-400 transition-colors"
                aria-label="Visit our Instagram page"
              >
                <Instagram className="w-5 h-5" aria-hidden="true" />
              </a>
              <a 
                href="mailto:info@wuma.photo" 
                className="text-gray-400 hover:text-orange-400 transition-colors"
                aria-label="Send us an email"
              >
                <Mail className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Quick Links</h3>
            <nav aria-label="Footer navigation">
              <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-orange-400 transition-colors text-sm sm:text-base">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/competition/2026" className="text-gray-300 hover:text-orange-400 transition-colors text-sm sm:text-base">
                  Current Competition
                </Link>
              </li>
              {submissionsOpen && (
                <li>
                  <Link to="/submit" className="text-gray-300 hover:text-orange-400 transition-colors text-sm sm:text-base">
                    Submit Photo
                  </Link>
                </li>
              )}
              <li>
                <Link to="/gallery" className="text-gray-300 hover:text-orange-400 transition-colors text-sm sm:text-base">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/rules" className="text-gray-300 hover:text-orange-400 transition-colors text-sm sm:text-base">
                  Rules & Guidelines
                </Link>
              </li>
              </ul>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Contact</h3>
            <div className="space-y-2 text-gray-300 text-sm sm:text-base">
              <div className="flex items-start space-x-2">
                <Mail className="w-4 h-4 mt-1 flex-shrink-0" />
                <span className="break-words">info@wuma.photo</span>
              </div>
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-4 h-4 flex-shrink-0 text-green-400" />
                <a 
                  href="https://wa.me/639566349270" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-orange-400 transition-colors"
                >
                  +639566349270
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>First Ever World Uw Macro Competition</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-blue-900/20 mt-6 sm:mt-8 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-gray-400 text-xs sm:text-sm">
            <p>&copy; 2026 World Underwater Macro Awards. Designed with love by Asiaqua Co Ltd. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="/privacy-policy" className="hover:text-orange-400 transition-colors">
                Privacy Policy
              </Link>
              <span>|</span>
              <Link to="/terms-of-service" className="hover:text-orange-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;